import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import PageHeader from '../components/PageHeader';
import { portfolioProjects } from '../mock';

export default function WorkPage() {
  return (
    <>
      <PageHeader
        index="// 02"
        eyebrow="Selected Work"
        title="Projects that turned data"
        accent="into measurable results."
        intro="A few of the products we've shipped across analytics, automation, and the web."
      />

      <section className="relative py-20 bg-surface overflow-hidden">
        <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bloom rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
            {portfolioProjects.map((project, i) => (
              <motion.article
                key={project.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 2) * 0.08, duration: 0.6 }}
                className="group rounded-2xl border border-line bg-raise overflow-hidden hover:border-purple-500/40 transition-all duration-300"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={project.image}
                    alt={`${project.title} cover`}
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <Badge className="bg-surface/60 backdrop-blur border border-line text-strong">
                      {project.category}
                    </Badge>
                    <span className="font-mono-ui text-[11px] text-soft">{project.year}</span>
                  </div>
                </div>

                <div className="p-7">
                  <h3 className="font-display text-2xl font-semibold text-strong mb-2 flex items-start justify-between gap-3">
                    {project.title}
                    <ArrowUpRight className="w-5 h-5 text-purple-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-1" />
                  </h3>
                  <p className="text-soft leading-relaxed mb-5">{project.description}</p>

                  <div className="flex flex-wrap gap-2 mb-5">
                    {project.technologies.map((t) => (
                      <span
                        key={t}
                        className="font-mono-ui text-[11px] px-2.5 py-1 rounded-full bg-raise2 border border-line text-purple-500"
                      >
                        {t}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 pt-4 border-t border-line text-pink-500 font-medium">
                    <ArrowRight className="w-4 h-4" />
                    {project.results}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="text-soft mb-6">Want to see your project here next?</p>
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-6 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/30 group"
              >
                Let's build it
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
